import { useState, useEffect, useMemo, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import toast from 'react-hot-toast'
import SimpleLexicalEditor from './simple-lexical-editor'
import { emailTemplate } from './template'
import useAppState from '@/contexts/state'
import { user as userService } from '@/services/user/user-service'
import type { LexicalEditor } from 'lexical'
import { $getRoot, $createParagraphNode } from 'lexical'
import SequenceSender from './sequence-sender'

type Mode = 'edit' | 'preview'

const DEFAULT_MESSAGE_HTML = `
<div class="body-content font-16">
  <p>Hi Dear Customer,</p>
  <br />
  <p>We have exciting news to share: we've just shipped a major AI upgrade! Whether you're working on proposals, contracts, policies or letters, our AI will now help you close deals smoother and faster.</p>
  <br />
  <p>Here's what's new and why it matters:</p>
  <br />
  <ul>
    <li>⚡️ <strong>Review your doc in under a minute</strong> → you can now save time reviewing documents of any length (size doesn't matter! 👀)</li>
    <li>🎯 <strong>Sharper risk cards with advanced filtering</strong> → with critical issues explained in detail, you can focus on protecting your business where it matters most with complete clarity</li>
    <li>🔄 <strong>Dismiss & reapply AI suggestions</strong> → you can keep fine-tuning your document by applying the improvements you like and dismissing the ones you don't (psst…if you change your mind, you can now re-apply deleted suggestions)</li>
    <li>📈 <strong>Broader risk coverage</strong> → catch and fix more issues now that more risks are detected across all your document types</li>
    <li>✨ <strong>Smoother navigation & clearer version comparisons</strong> → instantly see changes between drafts and easily move between them when you need to</li>
  </ul>
  <br />
  <p>We didn't want to keep you hanging ⏳ — so next time you upload or edit a document, you'll feel the difference immediately. Check it out below!</p>
  <br />
  <div style="text-align: center;">
    <a href="https://platform.cobrief.app/login" class="button font-14">Try It Now</a>
  </div>
</div>

<p class="font-16">
  <br />
  Cheers,
  <br />
  The Cobrief Team
</p>
`

export default function MarketingEmailsPage() {
  const [mode, setMode] = useState<Mode>('edit')
  const [activeTab, setActiveTab] = useState<'sequences' | 'custom'>('sequences')
  const [emails, setEmails] = useState('')
  const [subject, setSubject] = useState('Important Update from Cobrief')
  const [messageHtml, setMessageHtml] = useState(DEFAULT_MESSAGE_HTML)
  const [isSending, setIsSending] = useState(false)
  const user = useAppState(state => state.user)
  const editorRef = useRef<LexicalEditor | null>(null)

  const emailList = emails
    .split(',')
    .map(e => e.trim())
    .filter(Boolean)

  const handleFinalize = () => {
    if (!emails.trim()) {
      toast.error('Please enter at least one email address')
      return
    }
    if (!subject.trim()) {
      toast.error('Please enter an email subject')
      return
    }

    setActiveTab('custom') // Ensure we return to custom tab when going back
    setMode('preview')
  }

  const handleSend = () => {
    setIsSending(true)

    userService
      .sendMarketingEmails({
        emails: emailList,
        subject: subject,
        htmlContent: finalEmailHtml,
      })
      .then(response => {
        toast.success(`Emails sent: ${response.successCount} successful, ${response.failureCount} failed`)
        // setEmails('')
        // setSubject('')
        // handleClearEditor()
        setMode('edit')
      })
      .catch(error => {
        console.error('Error sending emails:', error)
        toast.error('Failed to send emails')
      })
      .finally(() => {
        setIsSending(false)
      })
  }

  const handleClearEditor = () => {
    if (editorRef.current) {
      editorRef.current.update(() => {
        const root = $getRoot()
        root.clear()
        const paragraph = $createParagraphNode()
        root.append(paragraph)
      })
      setMessageHtml('')
    }
  }

  useEffect(() => {
    setEmails(user?.email || '')
  }, [])

  const finalEmailHtml = useMemo(() => {
    return emailTemplate({
      appUrl: 'https://platform.cobrief.app',
      content: messageHtml,
    })
  }, [messageHtml])

  // Custom Email Preview Mode
  if (mode === 'preview') {
    return (
      <>
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold">Email Preview</h1>
          <p className="text-muted-foreground">
            Are you sure you want to send an email with the above content to {emailList.length}{' '}
            {emailList.length === 1 ? 'person' : 'people'}?
          </p>
        </div>

        <div className="mb-6 space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Recipients</h3>
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <p className="text-sm">{emails}</p>
                  </div>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Subject</h3>
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <p className="text-sm">{subject}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3 className="mb-4 text-sm font-semibold text-muted-foreground">Final Email Preview</h3>
              <div className="overflow-hidden rounded-lg border">
                <iframe
                  srcDoc={finalEmailHtml}
                  title="Email Preview"
                  className="h-[600px] w-full"
                  sandbox="allow-same-origin"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-3">
          <Button onClick={handleSend} disabled={isSending}>
            {isSending ? 'Sending...' : 'Yes, Send'}
          </Button>
          <Button variant="outline" onClick={() => setMode('edit')} disabled={isSending}>
            No, Back to Edit
          </Button>
        </div>
      </>
    )
  }

  // Main page with tabs
  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold">Marketing Emails</h1>
        <p className="text-muted-foreground">Send emails with Cobrief look and feel to one or multiple recipients</p>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'sequences' | 'custom')} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="sequences">Email Sequences</TabsTrigger>
          <TabsTrigger value="custom">Custom Email</TabsTrigger>
        </TabsList>

        <TabsContent value="sequences">
          <SequenceSender />
        </TabsContent>

        <TabsContent value="custom">
          <div className="space-y-6">
            <div>
              <label htmlFor="recipients" className="mb-2 block text-sm font-medium">
                Recipients
              </label>
              <Input
                id="recipients"
                type="text"
                placeholder="email1@example.com, email2@example.com"
                value={emails}
                onChange={e => setEmails(e.target.value)}
              />
              {emailList.length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">{emailList.length} recipient(s)</p>
              )}
            </div>

            <div>
              <label htmlFor="subject" className="mb-2 block text-sm font-medium">
                Subject
              </label>
              <Input
                id="subject"
                type="text"
                placeholder="Email subject"
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="message" className="mb-2 block text-sm font-medium">
                Email Content (This will get wrapped in the typical Cobrief email template)
              </label>

              <SimpleLexicalEditor
                key={mode} // Force remount when mode changes
                initialValue={messageHtml}
                onChange={setMessageHtml}
                onEditorReady={editor => (editorRef.current = editor)}
                placeholder="Enter your message here..."
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleFinalize}>Finalize</Button>
              <Button variant={'outline'} onClick={handleClearEditor}>
                Clear Editor
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </>
  )
}
